import { Project } from "utils/types"
import { parseAspectRatio } from "utils/scriptJSON"
import MetadataAwareTokenView from "components/MetadataAwareTokenView"
import PlatformBadgeRow from "components/PlatformBadgeRow"
import WorkSaleProgress from "components/WorkSaleProgress"
import { getProjectPlatformBadges } from "data/platforms"
import useProjectChainState from "hooks/useProjectChainState"
import { getContractChainId } from "utils/contractInfoHelper"
import {
  collaboratorLabel,
  getProjectStatus,
  isEffectivelyOpenEdition,
  toNumber,
} from "utils/projectDisplay"

interface Props {
  project: Project
  width?: number
  showDescription?: boolean
  statusLabel?: string
  soldOutEdition?: boolean
}

const ProjectPreview = ({
  project,
  width = 280,
  showDescription = false,
  statusLabel,
  soldOutEdition = false,
}: Props) => {
  const token = project?.tokens[0]
  const chainId = getContractChainId(project.contract.id)
  const chainState = useProjectChainState(project.contract.id, project.projectId, chainId)
  const displayProject = chainState.data
    ? {
        ...project,
        active: chainState.data.active ?? project.active,
        paused: chainState.data.paused ?? project.paused,
        complete: chainState.data.complete ?? project.complete,
        invocations: chainState.data.invocations as unknown as BigInt,
        maxInvocations: chainState.data.maxInvocations as unknown as BigInt,
      }
    : project
  const status = statusLabel || getProjectStatus(displayProject)
  const openEdition = isEffectivelyOpenEdition(displayProject)
  const soldCount = toNumber(displayProject.invocations)
  const totalCount = soldOutEdition || openEdition ? soldCount : toNumber(displayProject.maxInvocations)
  const platformBadges = getProjectPlatformBadges(project.contract.id, project.projectId, { chainId })

  return (
    <a
      href={`/project/${project.contract.id}/${project.projectId}`}
      className="gallery-card block group"
    >
      <div className="gallery-card-art">
        <div className="transition-transform duration-500 group-hover:scale-[1.015]">
          {token?.tokenId ? (
            <MetadataAwareTokenView
              contractAddress={project.contract.id}
              tokenId={token.tokenId}
              chainId={chainId}
              width={width}
              fallbackAspectRatio={project.aspectRatio || parseAspectRatio(project.scriptJSON)}
              showInvocationLink={false}
            />
          ) : (
            <div className="gallery-token-placeholder" style={{ width, height: width }}>
              <span>No works sold</span>
            </div>
          )}
        </div>
      </div>

      <div className="gallery-card-body">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="gallery-label artist-name text-gallery-muted">{collaboratorLabel(project.artistName)}</p>
            <h2 className="font-serif text-[1.55rem] leading-tight font-normal text-gallery-text group-hover:text-gallery-accent transition-colors duration-200">
              {project.name}
            </h2>
            <PlatformBadgeRow badges={platformBadges} interactive={false} className="mt-2" />
          </div>
          <span className="gallery-status">{status}</span>
        </div>

        {showDescription && project.description && (
          <p className="gallery-card-description">
            {project.description.replace(/\s+/g, " ").slice(0, 132)}
            {project.description.length > 132 ? "..." : ""}
          </p>
        )}

        <WorkSaleProgress
          soldCount={soldCount}
          totalCount={totalCount}
          loading={chainState.loading}
        />
      </div>
    </a>
  )
}

export default ProjectPreview
